/* eslint-disable filenames/match-exported */
import { SerializedError, unwrapResult } from "@reduxjs/toolkit";
import { ErrorAlert } from "components/ErrorAlert";
import { NaturalPersonIdentificationStatus } from "generated/globalTypes";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { typedUseSelector } from "store";
import idNowLogoEnabled from "../../../../../../assets/logos/idnow-logo-black.svg";
import solarisBankLogoEnabled from "../../../../../../assets/logos/solarisbank-logo-black.svg";
import FormControl from "../../../../../../components/FormControl/FormControl";
import { FormContent } from "../../../../../../components/FormContent";
import config from "../../../../../../config/index";
import { StepComponentProps } from "../../../../components/QuestionnaireController";
import DigitalIdentificationCard from "../../components/DigitalIdentificationCard";
import DigitalIdentificationFrame from "../../components/DigitalIdentificationFrame";
import { getLegalEntityIdentificationStatus } from "../actions";
import {
  IdentificationForm as IdentificationFormType,
  validationSchema,
} from "./identificationFormValidations";
import { Box, Icon, Text } from "@finvia/money-ui";
import * as S from "./Identification.styled";

const translationPrefix = "q-ai.mandate.form.identification";
const translationPrefixIdentificationStatus =
  "q-ai.mandate.form.identificationStatus";

const Form: React.FC<StepComponentProps> = ({ nextStep, previousStep }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<SerializedError | undefined>(undefined);
  const [digitalIdentificationSelected, setDigitalIdentificationSelected] =
    useState<boolean>(false);

  const [
    legalRepresentativeIdentificationStatus,
    setLegalRepresentativeIdentificationStatus,
  ] = useState<NaturalPersonIdentificationStatus | undefined>(undefined);

  const identificationStatusTimeout = useRef<NodeJS.Timeout | undefined>(
    undefined
  );

  const defaultData = typedUseSelector((state) => {
    return state.alternativeInvestments.mandateContractData.data.legalEntity
      .legalRepresentative;
  });

  const memoizedSchema = useMemo(() => validationSchema(t), [t]);
  const dispatch = useDispatch();

  const getLegalRepresentativeIdentificationStatus = useCallback(() => {
    dispatch(getLegalEntityIdentificationStatus())
      .then(unwrapResult)
      .then((identificationStatusData) => {
        switch (identificationStatusData?.legalRepresentative?.status) {
          case NaturalPersonIdentificationStatus.IDENTIFICATION_PENDING_SUCCESSFUL:
          case NaturalPersonIdentificationStatus.IDENTIFICATION_SUCCESSFUL:
            nextStep();
            break;
          case NaturalPersonIdentificationStatus.IDENTIFICATION_FAILED:
            setLegalRepresentativeIdentificationStatus(
              NaturalPersonIdentificationStatus.IDENTIFICATION_FAILED
            );
            break;
          default:
            identificationStatusTimeout.current = setTimeout(
              getLegalRepresentativeIdentificationStatus,
              config.polling.interval
            );
            break;
        }
      })
      .catch((e) => {
        // NOTE: fix to Access Token expiration; probable clock difference between the API and the Load Balancer.
        // After the API call fails, the Access Token is refreshed and the next call to the API will not fail.
        identificationStatusTimeout.current = setTimeout(
          getLegalRepresentativeIdentificationStatus,
          config.polling.interval
        );
      });
  }, [dispatch, nextStep]);

  useEffect(() => {
    getLegalRepresentativeIdentificationStatus();

    return () => {
      if (identificationStatusTimeout.current)
        clearTimeout(identificationStatusTimeout.current);
    };
  }, [getLegalRepresentativeIdentificationStatus]);

  const onSubmit = useCallback(async () => {
    nextStep();
  }, [nextStep]);

  return digitalIdentificationSelected ? (
    <DigitalIdentificationFrame
      url={defaultData?.url || ""}
      openDialog={
        legalRepresentativeIdentificationStatus ===
        NaturalPersonIdentificationStatus.IDENTIFICATION_PENDING
      }
    />
  ) : (
    <FormControl
      backLabel={t("form.back")}
      defaultValues={defaultData as Partial<IdentificationFormType>}
      onBack={previousStep}
      onSubmit={onSubmit}
      dontShowErrors
      validationSchema={memoizedSchema}
      dataAnalytics="3.3_le_start_ID-now"
    >
      {(formMethods) => {
        return (
          <FormContent
            title={t(`${translationPrefix}.title`)}
            subTitle={t(`${translationPrefix}.subTitle`)}
            isDirty={formMethods.formState.isDirty}
            isSubmitSuccessful={formMethods.formState.isSubmitSuccessful}
            error={error}
          >
            <DigitalIdentificationCard
              single
              data-testid="identificationType_DIGITAL_IDENTIFICATION"
              title={t(
                `${translationPrefix}.identificationType.DIGITAL_IDENTIFICATION.title`
              )}
              description={t(
                `${translationPrefix}.identificationType.DIGITAL_IDENTIFICATION.description`
              )}
              imageNode={
                <>
                  <Box>
                    <Icon name="video-identification" size="peta" />
                  </Box>
                  <img src={idNowLogoEnabled} alt="identification-icon" />
                </>
              }
              onIdentificationStart={async () => {
                setDigitalIdentificationSelected(true);
              }}
              dataAnalytics="3.3_le_start_idnow"
            >
              {legalRepresentativeIdentificationStatus ===
              NaturalPersonIdentificationStatus.IDENTIFICATION_FAILED ? (
                <ErrorAlert
                  customErrorMessage={t(
                    `${translationPrefixIdentificationStatus}.info.failed`
                  )}
                />
              ) : (
                <S.List>
                  <S.Item>
                    <Text>
                      {t(
                        `${translationPrefix}.identificationType.DIGITAL_IDENTIFICATION.downloadApp`
                      )}
                    </Text>
                  </S.Item>
                  <S.Item>
                    <Text>
                      {t(
                        `${translationPrefix}.identificationType.DIGITAL_IDENTIFICATION.internetConnection`
                      )}
                    </Text>
                  </S.Item>
                </S.List>
              )}
            </DigitalIdentificationCard>
          </FormContent>
        );
      }}
    </FormControl>
  );
};

export default Form;
